/**
 * S6 Markets, reduced to the chart. Task 31.
 *
 * `UI-Design-Spec.md#s6--markets` describes four things: a screener table over the universe,
 * a chart view, a correlation matrix, and a market-regime summary. **Only the chart is
 * buildable today** — the other three read `/screener`, `/correlations` and `/regime`, none
 * of which exist, because each needs computation over data nothing produces yet. So the
 * regime verdict that the spec makes this screen's hero is absent, and the note at the bottom
 * says so rather than a placeholder pretending to be one.
 *
 * That has a consequence worth stating: **this screen currently has no hero.** The spec's
 * "one hero figure per screen" rule is satisfied by having none rather than by promoting the
 * last price into the slot the regime verdict is supposed to occupy — a number in the hero
 * position is a claim that it is the conclusion the screen exists to deliver, and the last
 * price is not.
 *
 * ## Freshness comes from the server
 *
 * The age chip is not computed here. `/data-health` returns `freshness` per series, decided
 * by `jmt_marketdata.gates.freshness`, which delegates its stale bound to the same `is_stale`
 * the engine halts on. A chart that called a series fresh while the engine refused to
 * evaluate it would be worse than a chart with no chip.
 *
 * ## Live bars, and the filter that has to happen here (task 33)
 *
 * The topic is `prices:{symbol}`, so every timeframe for that symbol arrives on it — and they
 * arrive *together*, because `LiveIngest._publish_completed_windows` publishes the 1m bar and
 * every larger bar it just completed. So a chart on 1h receives the 1m, 5m and 15m bars too,
 * and applying one of them to the series would draw a minute's range as if it were an hour's.
 * The payload carries `timeframe` and this screen drops everything that is not the rung on
 * screen.
 *
 * Keyed on symbol rather than on symbol-and-timeframe deliberately (`jmt_api/topics.py`):
 * changing the dropdown is then a re-render and not a re-subscribe.
 */

import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { api } from '../api/client';
import { Timeframe, type BarResponse, type InstrumentResponse } from '../api/generated';
import { CandleChart } from '../charts/CandleChart';
import { useLiveConnection, useLiveTopic } from '../live/LiveProvider';
import { barFromPush, priceTopic } from '../live/prices';
import { ChartFrame } from '../design/components/data/ChartFrame.jsx';
import { Num } from '../design/components/data/Num.jsx';
import { FormField } from '../design/components/controls/FormField.jsx';
import { Select } from '../design/components/controls/Select.jsx';
import { CoinMark } from '../ui/CoinMark';
import { MODE_LABELS, useTimestampMode } from '../ui/timestamps';
import { EmptyState } from '../design/components/messages/EmptyState.jsx';
import { ErrorPanel } from '../design/components/messages/ErrorPanel.jsx';
import { Skeleton } from '../design/components/messages/Skeleton.jsx';
import { changePct, formatDecimal } from '../money';
import { PLACEHOLDER_COPY } from './Placeholder';

/** Seconds per timeframe, mirroring `jmt_domain.Timeframe`. */
const INTERVAL_SECONDS: Record<string, number> = {
  '1m': 60,
  '5m': 300,
  '15m': 900,
  '1h': 3_600,
  '4h': 14_400,
  '1d': 86_400,
};

/** `/bars` caps a request at 2,000 (`MAX_BAR_LIMIT`). */
const MAX_BARS = 2_000;

/**
 * 🔴 **`ChartFrame` draws `1d 7d 30d 1y all` whenever it is rendered, whether or not the caller
 * handles them.** This screen did not, so five buttons sat above every chart with hover states,
 * no current item and no handler — the exact dead affordance this screen's own docstring refuses
 * a search box for. They are wired here rather than hidden, because the range is a real question
 * about a real series and the data to answer it exists.
 *
 * A range is a **window in time**, and the fetch takes a bar count, so the two are related by the
 * timeframe: `1d` is 24 bars on `1h` and 1,440 on `1m`.
 */
const RANGE_SECONDS: Record<string, number> = {
  '1d': 86_400,
  '7d': 604_800,
  '30d': 2_592_000,
  '1y': 31_536_000,
};

/**
 * How many bars a range asks for, and whether the venue's cap truncated it.
 *
 * ⚠ **The truncation is returned rather than swallowed, and that is the point.** `1y` of `1m` bars
 * is 525,600 and the endpoint returns at most 2,000, so the chart would silently show a day and a
 * half under a button labelled `1y`. A range control that lies about its range is worse than no
 * range control, which is what this screen had.
 */
function barsFor(range: string, intervalSeconds: number): { limit: number; clamped: boolean } {
  const seconds = RANGE_SECONDS[range];
  if (seconds === undefined) return { limit: MAX_BARS, clamped: false };
  const wanted = Math.ceil(seconds / intervalSeconds);
  return { limit: Math.min(wanted, MAX_BARS), clamped: wanted > MAX_BARS };
}

export function MarketsScreen(): React.JSX.Element {
  const timestampMode = useTimestampMode();
  // Instrument and timeframe live in the URL, not in component state. "Look at BTC/EUR on
  // 1m" is a thing one operator says to another — and a thing an incident note has to be
  // able to record — so `/markets?instrument=31&timeframe=1m` has to be a real address.
  // Task 29 made every screen deep-linkable; a chart whose subject is invisible to the URL
  // would be the first exception.
  const [params, setParams] = useSearchParams();
  // `Number.isInteger` and not a bare `Number(...)`. A URL is user input, and `Number('abc')`
  // is `NaN` — which is not `null`, so it satisfied the `enabled` guard below and reached the
  // API as `/instruments/NaN`. Measured: the screen then rendered an error panel *and* an
  // empty state at once, saying two different things about the same failure, with the title
  // fallen back to "Chart". A malformed value is now indistinguishable from an absent one,
  // which is the honest reading: neither names an instrument.
  const requested = Number(params.get('instrument'));
  const instrumentId = Number.isInteger(requested) && requested > 0 ? requested : null;
  const timeframe = (params.get('timeframe') as Timeframe | null) ?? Timeframe._1H;
  // In the URL with the rest: "look at BTC/EUR on 1h over 30d" has to be one address.
  const range = params.get('range') ?? '30d';

  const setInstrumentId = (id: number) => {
    const next = new URLSearchParams(params);
    next.set('instrument', String(id));
    setParams(next, { replace: true });
  };
  const setTimeframe = (tf: Timeframe) => {
    const next = new URLSearchParams(params);
    next.set('timeframe', tf);
    setParams(next, { replace: true });
  };
  const setRange = (value: string) => {
    const next = new URLSearchParams(params);
    next.set('range', value);
    setParams(next, { replace: true });
  };

  // 🔴 **A filter, because the picker has 74 entries and was designed against three.**
  // See the block comment on the picker below.
  const [filter, setFilter] = useState('');

  const [theme, setTheme] = useState<'dark' | 'light'>(
    () => (document.documentElement.dataset.theme as 'dark' | 'light') ?? 'dark',
  );

  // The chart reads its colours from the tokens at construction, so it has to be told when
  // the operator flips the theme. `AppHeader` writes `data-theme` on the root element.
  useEffect(() => {
    const observer = new MutationObserver(() =>
      setTheme((document.documentElement.dataset.theme as 'dark' | 'light') ?? 'dark'),
    );
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  // **The one instrument, by id — not a page of the list.** The first version fetched
  // `listInstruments({limit: 500})` and searched it, which silently failed for every symbol
  // sorting past position 500 of 1,335: SOL/EUR rendered with the title "Chart" and no
  // symbol anywhere. `/instruments/{id}` is the endpoint for this, it is one row instead of
  // five hundred, and it cannot be wrong about a symbol that exists.
  const instrumentQuery = useQuery({
    queryKey: ['instrument', instrumentId],
    queryFn: () => api.marketData.getInstrument({ instrumentId: instrumentId as number }),
    enabled: instrumentId !== null,
  });

  // Whichever instrument actually has bars is not knowable from the instrument list — 1,335
  // are listed and 13 have been ingested — so the first pick comes from data-health, which
  // reports only series that exist. Picking `instruments[0]` would open on an empty chart
  // and read as a broken screen.
  const health = useQuery({
    queryKey: ['data-health'],
    queryFn: () => api.marketData.dataHealth({}),
  });

  useEffect(() => {
    if (instrumentId !== null) return;
    const first = health.data?.series[0];
    if (first) setInstrumentId(first.instrument_id);
    // `setInstrumentId` writes to the URL and is stable enough for this one-shot default;
    // adding it to the deps would re-run on every `params` change and fight the operator.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [health.data, instrumentId]);

  const window = barsFor(range, INTERVAL_SECONDS[timeframe] ?? 3_600);
  const bars = useQuery({
    // `range` is in the key: two ranges are two different series and must not share a cache entry.
    queryKey: ['bars', instrumentId, timeframe, range],
    queryFn: () =>
      api.marketData.listBars({
        instrumentId: instrumentId as number,
        timeframe,
        limit: window.limit,
      }),
    enabled: instrumentId !== null,
  });

  const instrument: InstrumentResponse | undefined = instrumentQuery.data;

  // --- live bars (task 33) ----------------------------------------------------
  //
  // Held in state rather than pushed into a ref inside the chart, so the whole path is one
  // React data flow: a push lands here, renders, and `CandleChart`'s effect calls `update()`.
  const [liveBar, setLiveBar] = useState<BarResponse | null>(null);
  const { live } = useLiveConnection();

  // Cleared when the subject changes. Without this, switching from BTC to ETH would leave the
  // last BTC candle queued and `CandleChart` would `update()` ETH's series with a BTC price —
  // a wrong number on the right chart, which is the worst shape of defect this product has.
  useEffect(() => {
    setLiveBar(null);
  }, [instrumentId, timeframe]);

  useLiveTopic(
    instrument ? priceTopic(instrument.symbol) : null,
    useCallback(
      (data: Record<string, unknown>) => {
        const push = barFromPush(data);
        // Dropped rather than coerced when the shape is wrong — see `prices.ts`. And filtered
        // by timeframe here, because every rung for this symbol arrives on one topic and they
        // arrive together; applying a 1m bar to an hourly series draws a minute's range as an
        // hour's.
        if (!push || push.timeframe !== timeframe) return;
        setLiveBar(push.bar);
      },
      [timeframe],
    ),
  );
  const series = health.data?.series.find(
    (row) => row.instrument_id === instrumentId && row.timeframe === timeframe,
  );
  const rows = bars.data?.bars ?? [];
  const first = rows[0];
  const fetchedLast = rows[rows.length - 1];

  /**
   * The newest bar the screen knows about, from either source.
   *
   * **Found by rendering it.** The figures below read the fetched series and the chart applies
   * live pushes, so a push moved the candle and left `Last` showing the price from before it:
   * measured on a real socket, the chart's own last-value label read `59500.40` while `Last`
   * read `53,639.6` — a 6,000 disagreement between two numbers on one screen about the same
   * thing. Every check in the suite was green, because nothing in it looks at two panels at
   * once.
   *
   * The chart is the one that cannot be wrong here — it holds whatever arrived most recently —
   * so the figures follow it rather than the other way round. `Change` is derived from the same
   * value for the same reason: a percentage against a stale close is a second wrong number.
   */
  const last =
    liveBar && (!fetchedLast || Date.parse(liveBar.ts) >= Date.parse(fetchedLast.ts))
      ? liveBar
      : fetchedLast;

  // A live bar either amends the last interval or opens a new one, and only the second adds to
  // the count. Compared on the timestamp rather than assumed, so the number matches the points
  // the chart actually holds.
  const barCount =
    liveBar && fetchedLast && Date.parse(liveBar.ts) > Date.parse(fetchedLast.ts)
      ? rows.length + 1
      : rows.length;

  /* The ingested set, deduplicated by symbol. `data-health` reports one row per series, so an
     instrument ingested on three timeframes appeared three times in the old dropdown. */
  const ingested = (health.data?.series ?? []).filter(
    (row, index, all) => all.findIndex((s) => s.symbol === row.symbol) === index,
  );
  const query = filter.trim().toUpperCase();
  const shown = query === '' ? ingested : ingested.filter((row) => row.symbol.includes(query));

  return (
    <div className="jmt-screen">
      {/* 🔴 **Chips with the coin's own mark, filtered, and capped in height.**
          The old picker was a `<Select>`, which is the wrong control for a set switched this
          often: two clicks, and the whole set hidden behind the first one. The marks earn their
          width because an operator finds ETH by its logo faster than by reading four rows of
          similar tickers.

          ⚠ **The first version of this shipped a real defect and it is worth naming.** It was
          designed against a fixture holding **three** instruments. Production ingests **74**, so
          the airy little chip row became fourteen rows that pushed the chart below the fold — on
          the operator's screen, never in a preview. `ScreenPreview`'s fixture now carries the
          real cardinality, because *a picker's fixture has to have the count the real screen has,
          not one row of each shape: layout defects live in the count.*

          So the set is filterable and the area is capped at a few rows and scrolls. Filtering is
          a `includes` on the symbol rather than a fuzzy match: the operator knows the ticker, and
          a fuzzy matcher that reorders results makes the position of a chip unstable between
          keystrokes.

          ⚠ 73 of the 74 traded instruments have a real mark since the marks moved to OKX's own
          CDN (2026-08-06, up from 43 via `cryptologos.cc`); the remainder falls back to a
          monogram tile. See `ui/CoinMark.tsx`. The chip still carries the symbol as text: the
          mark is a way in, never the only label — which is also what kept this honest while a
          third of it was monograms. */}
      <div className="jmt-row">
        <FormField
          label="Instrument"
          help="Only instruments with stored bars — 1,335 are listed and these are the ingested ones."
        >
          <div className="jmt-picker">
            <input
              type="search"
              className="jmt-picker__find"
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
              placeholder={`Filter ${ingested.length} instruments`}
              aria-label="Filter instruments"
              spellCheck={false}
            />
          <div className="jmt-picks" role="group" aria-label="Instrument">
            {ingested.length === 0 ? (
              <span className="jmt-picks__empty">No series has bars stored yet.</span>
            ) : null}
            {ingested.length > 0 && shown.length === 0 ? (
              <span className="jmt-picks__empty">
                No ingested instrument matches “{filter}”.
              </span>
            ) : null}
            {shown.map((row) => (
              <button
                type="button"
                key={row.instrument_id}
                className="jmt-pick"
                data-on={row.instrument_id === instrumentId ? '' : undefined}
                aria-pressed={row.instrument_id === instrumentId}
                onClick={() => setInstrumentId(row.instrument_id)}
              >
                <CoinMark symbol={row.symbol} size={20} />
                <span>{row.symbol}</span>
              </button>
            ))}
          </div>
          </div>
        </FormField>
        <FormField label="Timeframe" help="H3: a bar is stamped with its open time, always.">
          <Select
            value={timeframe}
            onChange={(next: string) => setTimeframe(next as Timeframe)}
            options={Object.keys(INTERVAL_SECONDS).map((tf) => ({ value: tf, label: tf }))}
            width={120}
          />
        </FormField>
      </div>

      {/* **The instrument's own failure is reported, and reported first.** It used to be
          swallowed: only `bars.isError` had a panel, so an id that does not exist — a stale
          bookmark, which is the common way this happens once a venue relists a pair — showed a
          frame titled "Chart" with no symbol and nothing saying why. The bars request failed
          too, so the operator got an error about *bars* for an instrument that is the actual
          problem. Naming the wrong subject is worse than naming none. */}
      {instrumentQuery.isError ? (
        <ErrorPanel
          message={`No instrument with id ${instrumentId}.`}
          detail={
            instrumentQuery.error instanceof Error
              ? instrumentQuery.error.message
              : String(instrumentQuery.error)
          }
          onRetry={() => void instrumentQuery.refetch()}
        />
      ) : bars.isError ? (
        <ErrorPanel
          message="Could not load bars for this series."
          detail={bars.error instanceof Error ? bars.error.message : String(bars.error)}
          onRetry={() => void bars.refetch()}
        />
      ) : null}

      <ChartFrame
        title={instrument ? `${instrument.symbol} · ${timeframe}` : 'Chart'}
        range={range}
        onRange={setRange}
        // The caption follows the axis, which follows the header. Three places, one answer.
      timezone={MODE_LABELS[timestampMode]}
        age={series ? formatAge(series.age_s) : undefined}
        ageState={series?.freshness}
        stale={series?.freshness === 'stale'}
        legend={legendFor(
          rows.filter((bar) => bar.synthetic).length,
          series?.open_gaps ?? 0,
          live,
          window.clamped ? { range, limit: window.limit } : null,
        )}
        height={460}
      >
        {bars.isPending && instrumentId !== null ? (
          <Skeleton height={420} />
        ) : /* **Not shown when a request failed.** "No bars stored" is a statement of fact
               about the data, and a failed request supports no such statement — the panel above
               already says what went wrong. Rendering both put two different explanations of
               one failure on screen at the same time, which is the defect this branch fixes. */
        bars.isError || instrumentQuery.isError ? null : rows.length === 0 ? (
          <EmptyState>
            {instrumentId === null
              ? 'Pick an instrument. Only those with stored bars are listed.'
              : 'No bars stored for this instrument at this timeframe. Data health lists the series that exist; a timeframe missing from it has never been aggregated.'}
          </EmptyState>
        ) : (
          <CandleChart
            bars={rows}
            intervalSeconds={INTERVAL_SECONDS[timeframe] ?? 3_600}
            theme={theme}
            height={420}
            liveBar={liveBar}
          />
        )}
      </ChartFrame>

      {first && last ? (
        <dl className="jmt-figures">
          {/* Every value here is the string the API sent, grouped for reading and never
              parsed. `toPlot` exists for the chart and is used nowhere else — see money.ts. */}
          <Figure label="First" value={formatDecimal(first.open)} />
          <Figure label="Last" value={formatDecimal(last.close)} />
          <Figure label="Change" value={changePct(first.open, last.close)} />
          <Figure label="Bars" value={String(barCount)} />
        </dl>
      ) : null}

      <p className="jmt-note">{PLACEHOLDER_COPY['markets']}</p>
    </div>
  );
}

/**
 * Seconds as the largest sensible unit — `AgeChip`'s own examples are `3s` and `4m`, and the
 * chip is a few characters wide. A day-old series rendered as `1500m` is arithmetic the
 * reader has to do, in the one place the design system built a component to avoid it.
 */
function formatAge(seconds: number): string {
  if (seconds < 90) return `${Math.max(0, Math.round(seconds))}s`;
  if (seconds < 5_400) return `${Math.round(seconds / 60)}m`;
  if (seconds < 172_800) return `${Math.round(seconds / 3_600)}h`;
  return `${Math.round(seconds / 86_400)}d`;
}

function Figure({ label, value }: { label: string; value: string }): React.JSX.Element {
  return (
    <div>
      <dt className="jmt-figure__label">{label}</dt>
      {/* `Num`'s own contract: "Every number in the UI goes through this." A **string** is
          returned untouched by its formatter, so the component supplies the tabular figures,
          the slashed zero and the ramp while the digits stay exactly what the API sent —
          passing a `number` here would be the float H6 excludes. */}
      <dd className="jmt-figure__value">
        <Num value={value} step="sm" />
      </dd>
    </div>
  );
}

/**
 * The chart's footnotes. The third one is a safety statement, not a status indicator.
 *
 * **A chart whose live updates have stopped looks exactly like a market that has stopped
 * moving.** Nothing else on this screen distinguishes the two: the candles are drawn, the age
 * chip comes from `/data-health` over REST and stays fresh, and the last price sits there
 * looking current. So the disconnection has to be said in words, next to the plot.
 *
 * Rendered conditionally and therefore instantly — `--dur-staleness` is `0ms`, and a trust
 * signal with a fade has an interval during which it is ambiguous.
 */
function legendFor(
  synthetic: number,
  openGaps: number,
  live: boolean,
  truncated: { range: string; limit: number } | null,
): { label: string; colour?: string }[] {
  const items: { label: string; colour?: string }[] = [];
  // 🔴 **A range the venue could not serve says so, here.** `1y` of `1m` bars is 525,600 and
  // `/bars` returns at most 2,000, so the chart would otherwise show a day and a half under a
  // button labelled `1y`. A range control that lies about its range is worse than the dead one
  // it replaced.
  if (truncated !== null) {
    items.push({
      label: `${truncated.range} is longer than this endpoint serves — showing the newest ${truncated.limit.toLocaleString('en-GB')} bars`,
      colour: 'var(--data-ink)',
    });
  }
  if (!live) {
    items.push({
      label: 'live updates disconnected — reconnecting; candles shown are from the last fetch',
      colour: 'var(--data-ink)',
    });
  }
  if (synthetic > 0) {
    items.push({
      label: `${synthetic} synthetic ${synthetic === 1 ? 'bar' : 'bars'} — manufactured, not observed`,
      colour: 'var(--data-ink)',
    });
  }
  if (openGaps > 0) {
    items.push({ label: `${openGaps} unfilled ${openGaps === 1 ? 'gap' : 'gaps'} in this series` });
  }
  return items;
}
